import React, { useState, useEffect, useMemo } from 'react';
import {
	Table,
	Input,
	Button,
	Dropdown,
	DropdownToggle,
	DropdownMenu,
	DropdownItem,
	Row,
	Col,
	Card,
	CardHeader,
	CardBody,
	CardFooter,
	FormGroup,
	Label,
	Alert,
	Nav,
	NavItem,
	NavLink,
	TabContent,
	TabPane
} from 'reactstrap';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import languageService from 'services/language.service';
import { useToolContext } from 'contexts/ToolContext';

// Thêm style global cho LanguageTable
const tableRowStyle = {
	cursor: 'pointer',
	transition: 'background-color 0.1s ease',
	userSelect: 'none', // Ngăn chặn việc bôi đen text khi click nhanh
};

const selectedRowStyle = {
	...tableRowStyle,
	backgroundColor: '#d3d3d3'
};

// Style cho bảng để tối ưu hiệu suất render
const tableStyle = {
	tableLayout: 'fixed', // Cố định layout của bảng
	borderCollapse: 'collapse',
	width: '100%',
};

// Style cho bảng
const cellStyle = {
	padding: '8px 12px',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
};

// Style cho input trong table khi edit inline
const cellInputStyle = {
	width: '100%',
	padding: '6px',
	border: '1px solid #ccc',
	borderRadius: '4px',
	boxSizing: 'border-box'
};

// Style cho Alert notification
const notificationStyle = {
	position: 'fixed',
	bottom: '20px',
	right: '20px',
	zIndex: 1050,
	minWidth: '300px',
	boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
	animation: 'fadeIn 0.3s ease-in'
};

// Helper function to normalize tool ID (remove _v5, _v7 suffixes)
const normalizeToolId = (toolId) => {
	if (!toolId) return toolId;
	return toolId.replace(/_v\d+$/, '');
};

function Language() {
	// Use global tool context
	const { lstTool, selectedTool, selectedToolName, setSelectedTool, isLoading } = useToolContext();

	// Local dropdown state
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const toggleDropdown = () => setDropdownOpen(prev => !prev);

	// Danh sách bản ghi ngôn ngữ và tìm kiếm
	const [languages, setLanguages] = useState([]);
	const [search, setSearch] = useState('');

	// State cho form thêm/cập nhật
	const [formKey, setFormKey] = useState('');
	const [formVi, setFormVi] = useState('');
	const [formEn, setFormEn] = useState('');

	// State cho chuyển tab
	const [activeTab, setActiveTab] = useState('form');
	const [jsonInput, setJsonInput] = useState('');
	const [jsonError, setJsonError] = useState('');

	// Sử dụng mảng để hỗ trợ multi-select và lưu vị trí dòng cuối được chọn (để hỗ trợ Shift)
	const [selectedRecords, setSelectedRecords] = useState([]);
	const [lastSelectedIndex, setLastSelectedIndex] = useState(null);

	// State cho edit inline
	const [editingRow, setEditingRow] = useState(null);
	const [editData, setEditData] = useState({ key: '', vi: '', en: '' });

	// State cho notification Alert
	const [notification, setNotification] = useState({
		visible: false,
		color: 'success',
		message: ''
	});

	// Load languages khi selectedTool thay đổi (normalize tool ID)
	useEffect(() => {
		if (!isLoading && selectedTool) {
			loadLanguages(normalizeToolId(selectedTool));
		}
	}, [isLoading, selectedTool]);

	// Tự động ẩn thông báo sau 3 giây
	useEffect(() => {
		let timer;
		if (notification.visible) {
			timer = setTimeout(() => {
				setNotification(prev => ({ ...prev, visible: false }));
			}, 3000);
		}
		return () => {
			if (timer) clearTimeout(timer);
		};
	}, [notification.visible]);

	function handleToolChange(tool, toolName) {
		setSelectedTool(tool, toolName, true);
	}

	const loadLanguages = (toolParam) => {
		languageService.getLanguage(toolParam)
			.then(res => {
				setLanguages(res.data);
				// Xóa lựa chọn khi load danh sách mới
				setSelectedRecords([]);
				setLastSelectedIndex(null);
				setEditingRow(null);
			})
			.catch(err => {
				showNotification('danger', `Lỗi tải dữ liệu: ${err.message}`);
				console.error(err);
			});
	};

	// Hiển thị thông báo
	const showNotification = (color, message) => {
		setNotification({
			visible: true,
			color,
			message
		});
	};

	// Đóng thông báo
	const onDismissNotification = () => {
		setNotification(prev => ({ ...prev, visible: false }));
	};

	const handleSearch = (e) => {
		setSearch(e.target.value);
	};

	// Sử dụng useMemo để tránh tính toán lại mỗi khi component re-render
	const filteredLanguages = useMemo(() => {
		return languages.filter(lang => {
			const keyLower = (lang.key || "").toLowerCase();
			const viLower = (lang.vi || "").toLowerCase();
			const enLower = (lang.en || "").toLowerCase();
			const searchLower = search.toLowerCase();
			return (
				keyLower.includes(searchLower) ||
				viLower.includes(searchLower) ||
				enLower.includes(searchLower)
			);
		});
	}, [languages, search]);

	// Xử lý chọn dòng (Ctrl/Shift hoặc click đơn)
	const handleRowClick = (record, index, event) => {
		// Ngăn chặn event bubbling
		event.preventDefault();
		event.stopPropagation();

		// Bỏ qua sự kiện click nếu đang ở chế độ sửa
		if (editingRow !== null) {
			return;
		}

		// Sử dụng tracking biến để chắc chắn chỉ xử lý lần click đầu tiên
		if (event.detail > 1) {
			// Double-click: Bắt đầu sửa inline
			handleRowDoubleClick(record, index);
			return;
		}

		let newSelected = [...selectedRecords];

		if (event.ctrlKey) {
			// Toggle dòng hiện tại
			const exists = newSelected.find(r => r.id === record.id);
			if (exists) {
				newSelected = newSelected.filter(r => r.id !== record.id);
			} else {
				newSelected.push(record);
			}
			setSelectedRecords(newSelected);
			setLastSelectedIndex(index);
		} else if (event.shiftKey && lastSelectedIndex !== null) {
			// Chọn tất cả các dòng từ lastSelectedIndex đến index hiện tại
			const start = Math.min(lastSelectedIndex, index);
			const end = Math.max(lastSelectedIndex, index);

			// Tạo mảng mới thay vì thay đổi mảng hiện tại để tránh re-render không cần thiết
			newSelected = [...newSelected];

			for (let i = start; i <= end; i++) {
				const rec = filteredLanguages[i];
				if (rec && !newSelected.some(r => r.id === rec.id)) {
					newSelected.push(rec);
				}
			}
			setSelectedRecords(newSelected);
		} else {
			// Click bình thường: chọn duy nhất dòng này
			newSelected = [record];
			setSelectedRecords(newSelected);
			setLastSelectedIndex(index);
		}

		// Cập nhật form: nếu chỉ có 1 dòng được chọn, đưa dữ liệu sang form
		if (newSelected.length === 1) {
			const sel = newSelected[0];
			setFormKey(sel.key || '');
			setFormVi(sel.vi || '');
			setFormEn(sel.en || '');
		} else {
			// Nếu nhiều dòng được chọn, xóa dữ liệu form
			setFormKey('');
			setFormVi('');
			setFormEn('');
		}
	};

	// Xử lý double click để edit inline
	const handleRowDoubleClick = (record, index) => {
		// Set dòng đang sửa và dữ liệu edit
		setEditingRow(record.id);
		setEditData({
			key: record.key || '',
			vi: record.vi || '',
			en: record.en || ''
		});

		// Đảm bảo chỉ có 1 dòng được chọn khi sửa
		setSelectedRecords([record]);
		setLastSelectedIndex(index);
	};

	// Cập nhật dữ liệu đang edit
	const handleEditChange = (field, value) => {
		setEditData(prev => ({
			...prev,
			[field]: value
		}));
	};

	// Hoàn thành edit và lưu thay đổi
	const handleEditComplete = () => {
		const editedRecord = selectedRecords[0];
		if (!editedRecord) return cancelEdit();

		// Sử dụng lại API cập nhật language nhưng với dữ liệu từ editData và id từ editedRecord
		languageService.updateLanguage(normalizeToolId(selectedTool), editedRecord.id, editData.key, editData.vi, editData.en)
			.then(res => {
				if (res.data.success) {
					showNotification('success', 'Cập nhật trực tiếp thành công');
					loadLanguages(normalizeToolId(selectedTool));
					clearForm(); // Xóa nội dung form bên trái sau khi cập nhật thành công
				} else {
					showNotification('danger', res.data.message || 'Cập nhật thất bại');
				}
			})
			.catch(err => {
				showNotification('danger', `Lỗi: ${err.message}`);
			})
			.finally(() => {
				setEditingRow(null);
			});
	};

	// Hủy edit
	const cancelEdit = () => {
		setEditingRow(null);
		setEditData({ key: '', vi: '', en: '' });
	};

	// Xử lý phím tắt khi đang edit
	const handleEditKeyDown = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			// Enter để hoàn thành
			e.preventDefault();
			handleEditComplete();
		} else if (e.key === 'Escape') {
			// Escape để hủy
			e.preventDefault();
			cancelEdit();
		}
	};

	// Xử lý chuột phải (context menu) - hiển thị modal xuất dữ liệu với layout 2 cột
	const handleContextMenu = (record, index, event) => {
		event.preventDefault();
		event.stopPropagation();

		// Nếu đang edit, không hiện context menu
		if (editingRow !== null) return;

		let currentSelection = selectedRecords;
		// Nếu dòng nhấn chuột phải chưa được chọn, chọn nó
		if (!currentSelection.some(r => r.id === record.id)) {
			currentSelection = [record];
			setSelectedRecords(currentSelection);
			setLastSelectedIndex(index);
		}

		// Hiển thị modal xuất dữ liệu
		showExportModal(currentSelection);
	};

	// Hàm hiển thị modal xuất dữ liệu với layout 2 cột
	const showExportModal = (currentSelection) => {
		// Tạo nội dung VB.NET
		const vbnetContent = currentSelection
			.map(r => `Public ${r.key} As String`)
			.join('\n');

		// Tạo nội dung JSON
		const jsonContent = JSON.stringify(
			currentSelection.map(r => ({
				key: r.key || '',
				vi: r.vi || '',
				en: r.en || ''
			})),
			null,
			2
		);

		Swal.fire({
			title: 'Xuất dữ liệu',
			html: `
				<div style="display: flex; gap: 20px; height: 400px;">
					<div style="flex: 1;">
						<h5 style="margin-bottom: 10px; color: #333;">Xuất key (VB.NET)</h5>
						<textarea
							id="vbnetTextarea"
							readonly
							style="
								width: 100%;
								height: 350px;
								font-family: monospace;
								font-size: 12px;
								line-height: 1.4;
								background-color: #f9f9f9;
								border: 1px solid #ddd;
								border-radius: 4px;
								padding: 10px;
								resize: none;
							"
						>${vbnetContent}</textarea>
					</div>
					<div style="flex: 1;">
						<h5 style="margin-bottom: 10px; color: #333;">Xuất dữ liệu JSON</h5>
						<textarea
							id="jsonTextarea"
							readonly
							style="
								width: 100%;
								height: 350px;
								font-family: monospace;
								font-size: 12px;
								line-height: 1.4;
								background-color: #f9f9f9;
								border: 1px solid #ddd;
								border-radius: 4px;
								padding: 10px;
								resize: none;
							"
						>${jsonContent}</textarea>
					</div>
				</div>
			`,
			width: '80%',
			maxWidth: '1200px',
			showConfirmButton: true,
			showDenyButton: true,
			showCancelButton: true,
			confirmButtonText: 'Sao chép VB.NET',
			denyButtonText: 'Sao chép JSON',
			cancelButtonText: `Xóa danh sách (${currentSelection.length})`,
			confirmButtonColor: '#3085d6',
			denyButtonColor: '#28a745',
			cancelButtonColor: '#dc3545',
			focusConfirm: true,
			footer: `
				<button id="closeBtn" class="swal2-styled" style="
					background-color: #6c757d; 
					color: white; 
					border: none; 
					padding: 10px 20px; 
					border-radius: 4px; 
					cursor: pointer;
					width: 200px;
				">
					Đóng
				</button>
			`,
			didOpen: () => {
				// Thêm event listener cho nút đóng
				const closeBtn = document.getElementById('closeBtn');
				if (closeBtn) {
					closeBtn.addEventListener('click', () => {
						Swal.close();
					});
				}
			},
			preConfirm: () => {
				const textArea = document.getElementById('vbnetTextarea');
				if (textArea) {
					textArea.select();
					document.execCommand('copy');
					showNotification('info', 'Đã sao chép VB.NET vào clipboard!');
				}
				return false;
			},
			preDeny: () => {
				const textArea = document.getElementById('jsonTextarea');
				if (textArea) {
					textArea.select();
					document.execCommand('copy');
					showNotification('info', 'Đã sao chép JSON vào clipboard!');
				}
				return false;
			}
		}).then((result) => {
			// Xử lý result sau khi modal đóng
			if (result.dismiss === Swal.DismissReason.cancel) {
				// Khi nhấn nút "Xóa danh sách" (cancel button)
				handleDeleteList(currentSelection);
			}
		});
	};

	const clearForm = () => {
		setSelectedRecords([]);
		setLastSelectedIndex(null);
		setFormKey('');
		setFormVi('');
		setFormEn('');
		setJsonInput('');
		setJsonError('');
	};

	// Validate JSON input
	const validateJson = (jsonString) => {
		try {
			const data = JSON.parse(jsonString);

			// Kiểm tra xem data có phải là mảng không
			if (!Array.isArray(data)) {
				setJsonError('Dữ liệu phải là một mảng các đối tượng');
				return false;
			}

			// Kiểm tra từng item trong mảng
			for (let i = 0; i < data.length; i++) {
				const item = data[i];
				if (!item.key || !item.vi || !item.en) {
					setJsonError(`Dòng ${i + 1}: Thiếu trường bắt buộc (key, vi, en)`);
					return false;
				}
			}

			setJsonError('');
			return true;
		} catch (e) {
			setJsonError('Lỗi cú pháp JSON: ' + e.message);
			return false;
		}
	};

	// Tạo nhiều bản ghi ngôn ngữ từ JSON
	const createLanguageFromJson = () => {
		if (!jsonInput.trim()) {
			setJsonError('Vui lòng nhập dữ liệu JSON');
			return;
		}

		if (validateJson(jsonInput)) {
			const jsonData = JSON.parse(jsonInput);

			languageService.createLanguageJson(selectedTool, jsonData)
				.then(res => {
					if (res.data.success) {
						showNotification('success', `Thêm thành công ${jsonData.length} bản ghi ngôn ngữ`);
						loadLanguages(selectedTool);
						clearForm();
					} else {
						showNotification('danger', res.data.message || 'Thêm mới thất bại');
					}
				})
				.catch(err => {
					showNotification('danger', `Lỗi: ${err.message}`);
				});
		}
	};

	const createLanguage = () => {
		languageService.createLanguage(selectedTool, formKey, formVi, formEn)
			.then(res => {
				if (res.data.success) {
					showNotification('success', 'Thêm mới ngôn ngữ thành công');
					loadLanguages(selectedTool);
					clearForm();
				} else {
					showNotification('danger', res.data.message || 'Thêm mới thất bại');
				}
			})
			.catch(err => {
				showNotification('danger', `Lỗi: ${err.message}`);
			});
	};

	const updateLanguage = () => {
		if (selectedRecords.length !== 1) {
			showNotification('warning', 'Vui lòng chọn một bản ghi để cập nhật');
			return;
		}

		const selectedRecord = selectedRecords[0];

		languageService.updateLanguage(selectedTool, selectedRecord.id, formKey, formVi, formEn)
			.then(res => {
				if (res.data.success) {
					showNotification('success', 'Cập nhật ngôn ngữ thành công');
					loadLanguages(selectedTool);
					clearForm();
				} else {
					showNotification('danger', res.data.message || 'Cập nhật thất bại');
				}
			})
			.catch(err => {
				showNotification('danger', `Lỗi: ${err.message}`);
			});
	};

	// Xóa theo id và key (dựa vào selectedRecords, nếu có nhiều dòng, xóa từng dòng)
	const deleteLanguage = () => {
		if (selectedRecords.length === 0) return;
		Swal.fire({
			title: 'Xác nhận xóa?',
			text: `Bạn có chắc muốn xóa ${selectedRecords.length} bản ghi?`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Có, xóa!',
			cancelButtonText: 'Hủy'
		}).then(result => {
			if (result.isConfirmed) {
				// Gọi API xóa cho từng bản ghi
				Promise.all(
					selectedRecords.map(rec =>
						languageService.deleteLanguage(selectedTool, rec.id, rec.key)
					)
				).then(responses => {
					// Nếu tất cả thành công
					const allSuccess = responses.every(r => r.data.success);
					if (allSuccess) {
						showNotification('success', 'Xóa ngôn ngữ thành công');
					} else {
						showNotification('danger', 'Có lỗi xảy ra khi xóa một số bản ghi');
					}
					loadLanguages(selectedTool);
					clearForm();
				}).catch(err => {
					showNotification('danger', `Lỗi: ${err.message}`);
				});
			}
		});
	};

	// Xóa danh sách bản ghi ngôn ngữ (sử dụng API deleteListLanguage)
	const handleDeleteList = (languageList) => {
		if (!languageList || languageList.length === 0) return;

		Swal.fire({
			title: 'Xác nhận xóa danh sách?',
			text: `Bạn có chắc muốn xóa ${languageList.length} bản ghi đã chọn?`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Có, xóa!',
			cancelButtonText: 'Hủy',
			confirmButtonColor: '#dc3545',
			cancelButtonColor: '#6c757d'
		}).then(result => {
			if (result.isConfirmed) {
				// Chuẩn bị dữ liệu gửi lên API (chỉ cần id và key)
				const deleteData = languageList.map(rec => ({
					id: rec.id,
					key: rec.key
				}));

				languageService.deleteListLanguage(selectedTool, deleteData)
					.then(res => {
						if (res.data.success) {
							const { total, deleted, errors } = res.data;
							if (errors.length === 0) {
								showNotification('success', `Xóa thành công ${deleted}/${total} bản ghi`);
							} else {
								showNotification('warning', `Xóa thành công ${deleted}/${total} bản ghi. Có ${errors.length} lỗi.`);
								console.log('Lỗi chi tiết:', errors);
							}
						} else {
							showNotification('danger', res.data.message || 'Xóa danh sách thất bại');
						}
						loadLanguages(selectedTool);
						clearForm();
					})
					.catch(err => {
						showNotification('danger', `Lỗi: ${err.message}`);
						console.error('Lỗi xóa danh sách:', err);
					});
			}
		});
	};

	const commitLanguage = () => {
		languageService.commitLanguage(selectedTool)
			.then(res => {
				if (res.data.success) {
					showNotification('success', 'Commit ngôn ngữ thành công');
				} else {
					showNotification('danger', res.data.message || 'Commit thất bại');
				}
			})
			.catch(err => {
				showNotification('danger', `Lỗi: ${err.message}`);
			});
	};

	return (
		<div className="content">
			<Row>
				{/* Bên trái: Form thêm/cập nhật ngôn ngữ */}
				<Col xs="12" sm="12" md="12" lg="4">
					<Card style={{ backgroundColor: '#fff', borderRadius: '20px', padding: '10px' }}>
						<CardHeader>
							<Label style={{ fontSize: '24px', color: 'DimGray', fontWeight: 'bold' }}>
								{selectedRecords.length === 1 ? "Cập nhật ngôn ngữ" : "Thêm mới ngôn ngữ"}
							</Label>
							{selectedRecords.length !== 1 && (
								<Nav tabs className="mt-2">
									<NavItem>
										<NavLink
											className={activeTab === 'form' ? 'active' : ''}
											onClick={() => setActiveTab('form')}
											style={{ cursor: 'pointer' }}
										>
											Form đơn lẻ
										</NavLink>
									</NavItem>
									<NavItem>
										<NavLink
											className={activeTab === 'json' ? 'active' : ''}
											onClick={() => setActiveTab('json')}
											style={{ cursor: 'pointer' }}
										>
											Nhập JSON
										</NavLink>
									</NavItem>
								</Nav>
							)}
						</CardHeader>
						<CardBody>
							{selectedRecords.length === 1 ? (
								// Form cập nhật ngôn ngữ
								<>
									<FormGroup>
										<Label for="langKey">Key</Label>
										<Input
											id="langKey"
											value={formKey}
											onChange={(e) => setFormKey(e.target.value)}
											placeholder="Nhập key"
											disabled={true}
										/>
									</FormGroup>
									<FormGroup>
										<Label style={{ marginTop: '10px' }} for="langVi">Vietnamese</Label>
										<Input
											type="textarea"
											id="langVi"
											rows="3"
											value={formVi}
											onChange={(e) => setFormVi(e.target.value)}
											placeholder="Nhập nội dung tiếng Việt"
										/>
									</FormGroup>
									<FormGroup>
										<Label style={{ marginTop: '10px' }} for="langEn">English</Label>
										<Input
											type="textarea"
											id="langEn"
											rows="3"
											value={formEn}
											onChange={(e) => setFormEn(e.target.value)}
											placeholder="Nhập nội dung tiếng Anh"
										/>
									</FormGroup>
								</>
							) : (
								// Form thêm mới với 2 tab
								<TabContent activeTab={activeTab}>
									<TabPane tabId="form">
										<FormGroup>
											<Label for="langKey">Key</Label>
											<Input
												id="langKey"
												value={formKey}
												onChange={(e) => setFormKey(e.target.value)}
												placeholder="Nhập key"
											/>
										</FormGroup>
										<FormGroup>
											<Label style={{ marginTop: '10px' }} for="langVi">Vietnamese</Label>
											<Input
												type="textarea"
												id="langVi"
												rows="3"
												value={formVi}
												onChange={(e) => setFormVi(e.target.value)}
												placeholder="Nhập nội dung tiếng Việt"
											/>
										</FormGroup>
										<FormGroup>
											<Label style={{ marginTop: '10px' }} for="langEn">English</Label>
											<Input
												type="textarea"
												id="langEn"
												rows="3"
												value={formEn}
												onChange={(e) => setFormEn(e.target.value)}
												placeholder="Nhập nội dung tiếng Anh"
											/>
										</FormGroup>
									</TabPane>
									<TabPane tabId="json">
										<FormGroup>
											<Label for="jsonInput">Nhập danh sách ngôn ngữ dạng JSON</Label>
											<Input
												type="textarea"
												id="jsonInput"
												rows="15"
												value={jsonInput}
												onChange={(e) => setJsonInput(e.target.value)}
												placeholder={`[
  {
    "key": "googleTranslateFree",
    "vi": "Google Dịch",
    "en": "Google Translate Free"
  },
  {
    "key": "anotherKey",
    "vi": "Giá trị Tiếng Việt",
    "en": "English Value"
  }
]`}
												style={{
													fontFamily: 'monospace',
													fontSize: '12px',
													whiteSpace: 'pre',
													minHeight: "450px",
												}}
											/>
										</FormGroup>
										{jsonError && (
											<Alert color="danger">{jsonError}</Alert>
										)}
									</TabPane>
								</TabContent>
							)}
						</CardBody>
						<CardFooter>
							{selectedRecords.length === 1 ? (
								<>
									<Button color="warning" onClick={updateLanguage}><i className="nc-icon nc-refresh-69" /> Cập nhật</Button>
									<Button color="danger" className="ml-2" onClick={deleteLanguage}><i className="nc-icon nc-simple-remove" /> Xóa</Button>
									<Button color="secondary" className="ml-2" onClick={clearForm}><i className="nc-icon nc-button-pause" /> Hủy</Button>
								</>
							) : (
								activeTab === 'form' ? (
									<Button color="success" onClick={createLanguage}><i className="nc-icon nc-simple-add" /> Thêm mới</Button>
								) : (
									<Button color="success" onClick={createLanguageFromJson}><i className="nc-icon nc-cloud-upload-94" /> Nhập từ JSON</Button>
								)
							)}
						</CardFooter>
					</Card>
				</Col>

				{/* Bên phải: Bảng danh sách ngôn ngữ */}
				<Col xs="12" sm="12" md="12" lg="8">
					<Card style={{ backgroundColor: '#fff', borderRadius: '20px', padding: '10px' }}>
						<CardHeader className="d-flex align-items-center">
							<Button color="success" onClick={commitLanguage}><i className="nc-icon nc-send" /> Commit</Button>
							{editingRow !== null && (
								<>
									<Button color="primary" className="ml-2" onClick={handleEditComplete}><i className="nc-icon nc-check-2" /> Lưu</Button>
									<Button color="secondary" className="ml-2" onClick={cancelEdit}><i className="nc-icon nc-simple-remove" /> Hủy</Button>
								</>
							)}
							<Input
								type="text"
								placeholder="Tìm kiếm..."
								value={search}
								onChange={handleSearch}
								className="ml-auto"
								style={{ width: '200px' }}
							/>
						</CardHeader>
						<CardBody>
							{/* Bỏ responsive để không hiển thị scrollbar không cần thiết */}
							<Table hover style={tableStyle}>
								<thead className="text-primary">
									<tr>
										<th style={cellStyle}>Key</th>
										<th style={cellStyle}>Vietnamese</th>
										<th style={cellStyle}>English</th>
									</tr>
								</thead>
								<tbody>
									{filteredLanguages.map((lang, index) => {
										const isSelected = selectedRecords.some(r => r.id === lang.id);
										const isEditing = editingRow === lang.id;

										return (
											<tr
												key={lang.id || index}
												style={isSelected ? selectedRowStyle : tableRowStyle}
												className={isSelected ? 'selected-row' : ''}
												onClick={(e) => handleRowClick(lang, index, e)}
												onDoubleClick={(e) => {
													e.preventDefault();
													e.stopPropagation();
													handleRowDoubleClick(lang, index);
												}}
												onContextMenu={(e) => handleContextMenu(lang, index, e)}
												data-testid={`language-row-${index}`}
											>
												<td style={cellStyle}>
													{isEditing ? (
														<Input
															type="text"
															value={editData.key}
															onChange={(e) => handleEditChange('key', e.target.value)}
															onKeyDown={handleEditKeyDown}
															style={cellInputStyle}
															autoFocus
														/>
													) : (
														lang.key
													)}
												</td>
												<td style={cellStyle}>
													{isEditing ? (
														<Input
															type="text"
															value={editData.vi}
															onChange={(e) => handleEditChange('vi', e.target.value)}
															onKeyDown={handleEditKeyDown}
															style={cellInputStyle}
														/>
													) : (
														lang.vi
													)}
												</td>
												<td style={cellStyle}>
													{isEditing ? (
														<Input
															type="text"
															value={editData.en}
															onChange={(e) => handleEditChange('en', e.target.value)}
															onKeyDown={handleEditKeyDown}
															style={cellInputStyle}
														/>
													) : (
														lang.en
													)}
												</td>
											</tr>
										);
									})}
								</tbody>
							</Table>
						</CardBody>
						{editingRow !== null && (
							<CardFooter className="text-center">
								<small className="text-muted">
									Nhấn Enter để lưu, ESC để hủy, hoặc click vào nút Lưu/Hủy
								</small>
							</CardFooter>
						)}
					</Card>
				</Col>
			</Row>

			{/* Thông báo Alert ở góc phải màn hình */}
			{notification.visible && (
				<Alert
					color={notification.color}
					style={notificationStyle}
					toggle={onDismissNotification}
					fade={true}
				>
					{notification.message}
				</Alert>
			)}
		</div>
	);
}

export default Language;